import React from 'react';
import { Receipt, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface ExpenseCategory {
    id: string;
    label: string;
}

interface ExpenseReport {
    id: string;
    amount: number;
    category: string;
    description: string;
    receipt_document_id: string | null;
    status: 'pending' | 'approved' | 'refused' | 'reimbursed';
    refuse_reason: string;
    submitted_at: string;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
    pending: { label: 'En attente', cls: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    approved: { label: 'Approuvée', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    refused: { label: 'Refusée', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    reimbursed: { label: 'Remboursée', cls: 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border-brand-500/20' },
};

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const EmployeeExpenses: React.FC = () => {
    const [reports, setReports] = React.useState<ExpenseReport[]>([]);
    const [categories, setCategories] = React.useState<ExpenseCategory[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showForm, setShowForm] = React.useState(false);
    const [amount, setAmount] = React.useState('');
    const [category, setCategory] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [receiptFile, setReceiptFile] = React.useState<File | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = () => {
        moduleApi.getExpenseReports().then((d: any) => setReports(Array.isArray(d) ? d : [])).catch(() => setReports([])).finally(() => setLoading(false));
        moduleApi.getExpenseCategories().then((d: any) => setCategories(Array.isArray(d) ? d : [])).catch(() => setCategories([]));
    };
    React.useEffect(() => { load(); }, []);

    // window.open() doit s'exécuter de façon SYNCHRONE au clic (avant tout await) sous peine d'être
    // bloqué silencieusement par le navigateur (geste utilisateur perdu après le fetch).
    const viewReceipt = async (reportId: string, win: Window | null) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/expense-reports/${reportId}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); }
    };

    const resetForm = () => {
        setAmount(''); setCategory(categories[0]?.label || ''); setDescription(''); setReceiptFile(null); setError('');
    };

    const handleSubmit = async () => {
        const value = Number(amount);
        if (!value || value <= 0) { setError('Montant requis.'); return; }
        setSubmitting(true);
        setError('');
        try {
            let receipt: { display_name: string; file_name: string; mime_type: string; data: string } | undefined;
            if (receiptFile) {
                const data = await fileToBase64(receiptFile);
                receipt = { display_name: receiptFile.name, file_name: receiptFile.name, mime_type: receiptFile.type || 'application/octet-stream', data };
            }
            const res: any = await moduleApi.createExpenseReport({ amount: value, category, description: description.trim(), receipt });
            if (res?.error) { setError(res.error); return; }
            setShowForm(false);
            resetForm();
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><Receipt size={24} /> Notes de frais</h1>
                    <p className="text-content-muted mt-1">Soumettez vos dépenses professionnelles avec justificatif.</p>
                </div>
                <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold transition-all">
                    <Plus size={16} /> Nouvelle note de frais
                </button>
            </header>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : reports.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <Receipt size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucune note de frais pour l'instant.</p>
                </div>
            ) : (
                <div className="max-w-2xl space-y-2">
                    {reports.map((r) => {
                        const status = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
                        return (
                            <div key={r.id} className="p-4 rounded-2xl bg-surface-soft border border-border space-y-1.5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-bold text-content text-sm">{formatPrice(r.amount)} <span className="text-content-muted font-normal">— {r.category}</span></p>
                                        <p className="text-[11px] text-content-muted mt-0.5">Envoyée le {new Date(r.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <span className={`shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${status.cls}`}>{status.label}</span>
                                </div>
                                {r.description && <p className="text-xs text-content">{r.description}</p>}
                                {r.status === 'refused' && r.refuse_reason && (
                                    <p className="text-xs text-red-400"><span className="font-bold">Motif :</span> {r.refuse_reason}</p>
                                )}
                                {r.receipt_document_id && (
                                    <button onClick={() => viewReceipt(r.id, window.open('', '_blank'))}
                                        className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                        <Paperclip size={11} /> Justificatif
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="text-base font-bold text-content flex items-center gap-2"><Receipt size={17} className="text-brand-500" /> Nouvelle note de frais</h2>
                            <button onClick={() => setShowForm(false)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Montant (€)</label>
                                <input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" placeholder="Ex: 45.90" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Catégorie</label>
                                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500">
                                    {categories.map((cat) => <option key={cat.id} value={cat.label}>{cat.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Description (optionnel)</label>
                                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Photo du justificatif (optionnel)</label>
                                <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface-soft border border-border text-sm text-content-muted hover:text-content cursor-pointer transition-colors">
                                    <Paperclip size={14} /> {receiptFile ? receiptFile.name : 'Choisir un fichier'}
                                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                                </label>
                            </div>
                            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                        </div>
                        <div className="flex justify-end gap-3 p-5 border-t border-border">
                            <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                            <button onClick={handleSubmit} disabled={submitting} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50">
                                {submitting ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeExpenses;
